import React, { useState, useContext } from 'react';
import { useGeneralContext } from '@context/GeneralContext';
import { useTranslation } from 'react-i18next';
import localStorageNames from '@data/localStorageNames';

// realm
import { ObjectId } from 'bson';
import { generalErrorHandler } from '@functions/errorHandler';
import { sendErrorData } from '@functions/ErrorMessagesSender';
import { useStoreFirstCredential } from '@state/useStoreFirstCredential';
import { useStoreOutlet } from '@state/useStoreOutlet';
import useHITApi from '@hooks/useHITApi';

// const REALM_APP_ID = import.meta.env[
//   `VITE_REACT_APP_REALM_CONFIGURATION_${import.meta.env.MODE}`
// ];
const GET_PRODUCT_GROUP = import.meta.env[
  `VITE_REACT_APP_GET_PRODUCT_GROUP_${import.meta.env.MODE}`
];
const CLIENT_SERVICE_CHARGE = import.meta.env[
  `VITE_REACT_APP_GET_CLIENT_SERVICE_CHARGE_${import.meta.env.MODE}`
];
const GET_BILL = import.meta.env[
  `VITE_REACT_APP_GET_CLIENT_BILL_${import.meta.env.MODE}`
];
const GET_BILL_DETAIL = import.meta.env[
  `VITE_REACT_APP_GET_CLIENT_BILL_DETAIL_${import.meta.env.MODE}`
];
const CLIENT_TRANSACTION = import.meta.env[
  `VITE_REACT_APP_ENDPOINT_CLIENT_TRANSACTION_${import.meta.env.MODE}`
];
const ADYEN_SESSION = import.meta.env[
  `VITE_REACT_APP_ENDPOINT_GET_ADYEN_SESSION_${import.meta.env.MODE}`
];
const XENDIT_PAYMENT_LINK = import.meta.env[
  `VITE_REACT_APP_ENDPOINT_CHECK_XENDIT_PAYMENT_LINK_${import.meta.env.MODE}`
];
const SNAP_PAYMENT = import.meta.env[
  `VITE_REACT_APP_ENDPOINT_CREATE_SNAP_PAYMENT_${import.meta.env.MODE}`
];

const TRANSACTIONS_STATUS = import.meta.env[
  `VITE_REACT_APP_ENDPOINT_TRANSACTIONS_STATUS_${import.meta.env.MODE}`
];

// The fe-lambda endpoints the container's SNAP payment page calls. Handed to it with the
// payment because prod A and B share one container but not their fe-lambda: these
// resolve to this build's own variant.
export const SNAP_ENDPOINTS = {
  createSnapPayment: SNAP_PAYMENT,
  checkTransactionStatus: TRANSACTIONS_STATUS,
  // Asks the bank, through fe-lambda and the SNAP proxy, whether the bill was paid
  // (fe-lambda 0.5.25): same fe-lambda as checkTransactionStatus, so derived from it.
  verifySnapPayment: TRANSACTIONS_STATUS
    ? TRANSACTIONS_STATUS.replace(/\/checkTransactionStatus\/?$/, '/verifySnapPayment')
    : undefined,
};
const SEND_EMAIL_BILL = import.meta.env[
  `VITE_REACT_APP_ENDPOINT_SEND_EMAIL_BILL_${import.meta.env.MODE}`
];
// const realmApp = new Realm.App({ id: REALM_APP_ID });

export const LambdaFunctionContext = React.createContext([{}, () => { }]);

const LambdaFunctionProvider = ({ children }) => {
  const { openGeneralModal, showLoading } = useGeneralContext();
  const { t } = useTranslation();
  const [state, setState] = useState({
    transaction: {},
    firstOpenApp: true,
    messageRetry: '',
  });

  const hitAPI = useHITApi();

  const setLocalState = (newData) => {
    setState((prev) => ({
      ...prev,
      ...newData,
    }));
  };

  const getLocalState = (key) => {
    if (key) {
      return state[key];
    }
    return state;
  };

  const { credentials } =
    useStoreFirstCredential((state) => ({
      credentials: state.credentials,
    })); 

  const {
    getOutletInfo: getOutletData,
    getTableName,
  } = useStoreOutlet((state) => ({
    getOutletInfo: state.getOutletInfo,
    getTableName: state.getTableName,
  }));
 
  const getTransactionsId = () => {
    return JSON.parse(localStorage.getItem(localStorageNames.TRANSACTIONS_ID));
  };

  const saveTransactionsId = async (transactionId) => {
    let tempTransactionsId = [];
    if (getTransactionsId()) {
      tempTransactionsId = getTransactionsId();
    }
    tempTransactionsId.push(transactionId);

    localStorage.setItem(
      localStorageNames.TRANSACTIONS_ID,
      JSON.stringify(tempTransactionsId),
    );
  };

  const callClientTransaction = async (payloadData) => {
    const now = new Date();
    const timeString = now.toLocaleTimeString(getOutletData().localeCode, { hour12: false });
    console.log('HIT client trans--------- at :', timeString);
    try {
      const payload = {
        method: 'POST',
        data: {
          ...payloadData,
        },
      }
      return await hitAPI(CLIENT_TRANSACTION, 'POST', payload, true);
    } catch (error) {
      showLoading(false);
    }
  };

  const promiseHitTransaction = (payloadData, counter) => {
    console.log('promise trans----------');
    return new Promise(async (resolve, reject) => {
      if (counter === 3) {
        reject({ message: 'failed to fetch' });
        return;
      }
      const result = await callClientTransaction(payloadData);
      if (result) {
        resolve(result);
      } else {
        setTimeout(async () => {
          try {
            counter++;
            const result = await promiseHitTransaction(
              payloadData,
              counter,
            );
            resolve(result);
          } catch (error) {
            reject(error);
          }
        }, 1000);
      }
    });
  };

  const hitTransaction = async (payloadData) => {
    if (credentials) {
      console.log('hit transaction--------');
      let result;
      try {
        result = await promiseHitTransaction(payloadData, 0);

        // The order was refused because a product sold out or left the menu while the
        // customer had the page open. Returned, not thrown: throwing flattens it to a
        // string and the item list — what the customer has to remove — is lost.
        if (result?.error && result?.reason_code === 'items_unavailable') {
          return {
            errorMessage: result.message,
            errorStatus: 'items_unavailable',
            unavailableItems: Array.isArray(result.items) ? result.items : [],
          };
        }

        // Check if result is valid before saving
        if (!result || result.error) {
          throw new Error(result?.message || 'Invalid transaction result');
        }
        await saveTransactionsId(result);
        return result;
      } catch (error) {
        // generalErrorHandler(error, url, callback);
        let unexpectedError = generalErrorHandler(error, null, () =>
          openGeneralModal({
            title: t('networkError'),
            content: t('errorParameter'),
          }),
        );

        if (unexpectedError) {
          sendErrorData({
            message: `${error.message} | ${result ? JSON.stringify(result.json()) : 'null result from server'
              }`,
            table: getTableName(),
            file: 'LambdaFunctionContext.js',
            func: 'hitTransaction()',
          });
        }

        return { errorMessage: error.message, errorStatus: 'parameter' };
      }
    }
  };

  // TEMPORARILY COMMENTED OUT - FOR FUTURE USE
  // const hitRealmTransactionStripe = async (data) => {
  //   const credentials = Realm.Credentials.function(checkValidCredentials());
  //   const user = await realmApp.logIn(credentials);
  //   const result = await user.callFunction('stripePaymentSucces', {
  //     method: 'POST',
  //     _id: data.id,
  //     SubFunctionID: parseInt(data.subFunctionID),
  //   });
  //   // result true || false
  //   return result;
  // };

  const isLastTransactionExist = async (filter) => {
    console.log('RF IS_LAST_TRANSACTION_EXISTS---------');
    try {
      const data = {
        method: 'IS_LAST_TRANSACTION_EXISTS',
        filter: { ...filter },
      }
      return await hitAPI(GET_BILL, 'POST', data, true);
    } catch (error) {
      showLoading(false);
    }
  };

  const getLastTransaction = async (filter) => {
    console.log('RF get last trans-----');
    const data = {
      method: 'GET_LAST_TRANSACTION',
      filter: { ...filter },
    }
    return await hitAPI(GET_BILL, 'POST', data, true);
  };

  const getDetailTransaction = async (filter) => {
    console.log('RF get detail-----');
    const data = {
      method: 'GET_DETAIL',
      filter: { ...filter },
    }
    return await hitAPI(GET_BILL_DETAIL, 'POST', data, true);
  };

  const getSessionAdyen = async (filter) => {
    console.log('RF get session adyen-----');
    return await hitAPI(ADYEN_SESSION, 'POST', filter, true);
  };

  const createXenditPaymentLinkAPI = async (payloadData) => {
    console.log('RF create xendit payment link-----');
    return await hitAPI(XENDIT_PAYMENT_LINK, 'POST', payloadData, true);
  };

  const getHistoryTransaction = async (filter) => {
    console.log('RF history trans-----');
    const data = {
      method: 'HISTORY',
      filter: { ...filter },
    }
    return await hitAPI(GET_BILL, 'POST', data, true);
  };

  const getHistoryPaymentOrder = async () => {
    let outletInfo = getOutletData();
    let transactionsId = getTransactionsId();
    const mapTransId = transactionsId.map((data) => {
      return new ObjectId(data);
    });
    const filterById = {
      $in: mapTransId,
    };
    const data = {
      method: 'HISTORY_VIEW',
      filter: { _id: filterById, table_id: outletInfo.tableId },
    }
    return await hitAPI(GET_BILL, 'POST', data, true);
  };

  const getProductGroup = async (filter) => {
    console.log('RF product group --------');
    return await hitAPI(GET_PRODUCT_GROUP, 'POST', filter, true);
  };

  const getServiceCharge = async (filter) => {
    console.log('RF service charge --------');
    return await hitAPI(CLIENT_SERVICE_CHARGE, 'POST', filter, true)
  };

  const sendEmailBill = async (filter) => {
    console.log('RF send email bill --------');
    return await hitAPI(SEND_EMAIL_BILL, 'POST', filter, true);
  };

  const checkTransactionStatus = async (filter) => {
    const now = new Date();
    const timeString = now.toLocaleTimeString(getOutletData().localeCode, { hour12: false });
    console.log(`HIT checkTransactionStatus -------- [${timeString}]`);
    return await hitAPI(TRANSACTIONS_STATUS, 'POST', filter, true)
  };

  return (
    <LambdaFunctionContext.Provider
      value={{
        getLocalState,
        setLocalState,
        hitTransaction,
        // TEMPORARILY COMMENTED OUT - FOR FUTURE USE
        // hitRealmTransactionStripe,
        getTransactionsId,
        saveTransactionsId,
        isLastTransactionExist,
        getLastTransaction,
        getDetailTransaction,
        getHistoryTransaction,
        getHistoryPaymentOrder,
        getSessionAdyen,
        createXenditPaymentLinkAPI,
        getProductGroup,
        getServiceCharge,
        checkTransactionStatus,
        sendEmailBill
      }}>
      {children}
    </LambdaFunctionContext.Provider>
  );
};

export const useLambdaFunctionContext = () => {
  const value = useContext(LambdaFunctionContext);
  if (value == null) {
    throw new Error('useCartContext() called outside of a Provider?');
  }
  return value;
};

export default LambdaFunctionProvider;